import React, { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { kr } from '../lib/format';
import { PageHeader, Card, StatCard, Modal } from '../components/ui';
import { Package, AlertTriangle, ClipboardList, Sliders, History, TrendingUp } from 'lucide-react';

const Lager = () => {
  const [varer, setVarer] = useState([]);
  const [verdi, setVerdi] = useState(null);
  const [bareLav, setBareLav] = useState(false);
  const [tellingApen, setTellingApen] = useState(false);
  const [tellingLinjer, setTellingLinjer] = useState({});
  const [tellingNotat, setTellingNotat] = useState('Varetelling');
  const [justering, setJustering] = useState(null);
  const [histApen, setHistApen] = useState(null);
  const [hist, setHist] = useState([]);

  const last = useCallback(() => {
    api.get('/lager', { params: bareLav ? { lav: 1 } : {} }).then((r) => setVarer(r.data));
    api.get('/lager/verdi').then((r) => setVerdi(r.data));
  }, [bareLav]);
  useEffect(() => { last(); }, [last]);

  const apneTelling = () => {
    api.get('/lager').then((r) => {
      const init = {};
      r.data.forEach((v) => { init[v.id] = v.antall; });
      setTellingLinjer(init); setTellingApen(true);
    });
  };

  const lagreTelling = async () => {
    const linjer = Object.entries(tellingLinjer).map(([id, talt]) => ({
      produkt_id: Number(id), talt_antall: parseFloat(talt) || 0,
    }));
    await api.post('/lager/varetelling', { linjer, notat: tellingNotat });
    setTellingApen(false); last();
  };

  const lagreJustering = async () => {
    if (!justering) return;
    await api.post('/lager/justering', {
      produkt_id: justering.id,
      antall: parseFloat(justering.delta) || 0,
      kostpris: parseFloat(justering.kostpris) || undefined,
      notat: justering.notat,
    });
    setJustering(null); last();
  };

  const apneHistorikk = (v) => {
    setHistApen(v);
    api.get(`/lager/${v.id}/historikk`).then((r) => setHist(r.data));
  };

  return (
    <>
      <PageHeader
        title="Lager"
        subtitle="Beholdning, varetelling og lagerverdi"
        actions={<>
          <button className="btn-ghost" onClick={apneTelling}>
            <ClipboardList size={14} /> Varetelling
          </button>
        </>}
      />

      {verdi && (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
          <StatCard label="Lagerverdi" value={kr(verdi.totalverdi)} color="var(--theme-accent)" icon={TrendingUp} />
          <StatCard label="Antall varer" value={verdi.antall_varer} color="var(--theme-text)" icon={Package} />
          <StatCard label="Lav beholdning" value={verdi.lav_beholdning} color="var(--theme-warning)" icon={AlertTriangle} />
        </div>
      )}

      <Card style={{ marginBottom: 12 }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, margin: 0 }}>
          <input type="checkbox" checked={bareLav} onChange={(e) => setBareLav(e.target.checked)} />
          Vis kun varer med lav beholdning
        </label>
      </Card>

      <Card style={{ padding: 0 }}>
        <table>
          <thead>
            <tr>
              <th>Vare</th><th>Enhet</th>
              <th style={{ textAlign: 'right' }}>Beholdning</th>
              <th style={{ textAlign: 'right' }}>Min</th>
              <th style={{ textAlign: 'right' }}>Kostpris</th>
              <th style={{ textAlign: 'right' }}>Verdi</th>
              <th>Konto</th><th style={{ width: 120 }}></th>
            </tr>
          </thead>
          <tbody>
            {varer.length === 0 && (
              <tr><td colSpan={8} className="fm-text-muted" style={{ textAlign: 'center', padding: 20 }}>
                Ingen varer registrert. Marker produkter som «vare» under Produkter.
              </td></tr>
            )}
            {varer.map((v) => (
              <tr key={v.id}>
                <td>{v.navn}</td>
                <td>{v.enhet}</td>
                <td style={{ textAlign: 'right' }}>
                  {v.lav && <AlertTriangle size={11} style={{ color: 'var(--theme-warning)' }} />} {v.antall}
                </td>
                <td style={{ textAlign: 'right' }}>{v.min_lager || '—'}</td>
                <td style={{ textAlign: 'right' }}>{kr(v.kostpris)}</td>
                <td style={{ textAlign: 'right' }}>{kr(v.verdi)}</td>
                <td>{v.lager_konto}</td>
                <td>
                  <div className="flex" style={{ gap: 4 }}>
                    <button className="btn-ghost" title="Juster"
                            onClick={() => setJustering({ id: v.id, navn: v.navn, delta: 0, kostpris: v.kostpris, notat: '' })}>
                      <Sliders size={12} />
                    </button>
                    <button className="btn-ghost" title="Historikk" onClick={() => apneHistorikk(v)}>
                      <History size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Varetelling */}
      <Modal
        open={tellingApen} onClose={() => setTellingApen(false)} width={720}
        title="Varetelling"
        footer={<>
          <button className="btn-ghost" onClick={() => setTellingApen(false)}>Avbryt</button>
          <button className="btn-primary" onClick={lagreTelling}>Bokfør telling</button>
        </>}
      >
        <div style={{ marginBottom: 10 }}>
          <label>Notat</label>
          <input value={tellingNotat} onChange={(e) => setTellingNotat(e.target.value)} />
        </div>
        <table>
          <thead><tr><th>Vare</th><th style={{ textAlign: 'right' }}>System</th>
                     <th style={{ textAlign: 'right', width: 120 }}>Telt antall</th></tr></thead>
          <tbody>
            {varer.map((v) => (
              <tr key={v.id}>
                <td>{v.navn}</td>
                <td style={{ textAlign: 'right' }}>{v.antall}</td>
                <td>
                  <input type="number" step="0.01"
                         value={tellingLinjer[v.id] ?? v.antall}
                         onChange={(e) => setTellingLinjer({ ...tellingLinjer, [v.id]: e.target.value })}
                         style={{ textAlign: 'right' }} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="fm-text-muted" style={{ fontSize: 11, marginTop: 8 }}>
          Differansen mellom system og telt antall registreres som lagerbevegelse «varetelling».
        </p>
      </Modal>

      {/* Justering */}
      <Modal
        open={!!justering} onClose={() => setJustering(null)}
        title={`Juster lager – ${justering?.navn || ''}`}
        footer={<>
          <button className="btn-ghost" onClick={() => setJustering(null)}>Avbryt</button>
          <button className="btn-primary" onClick={lagreJustering}>Lagre</button>
        </>}
      >
        {justering && (
          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label>Endring (+/-)</label>
              <input type="number" step="0.01" value={justering.delta}
                     onChange={(e) => setJustering({ ...justering, delta: e.target.value })} />
            </div>
            <div>
              <label>Kostpris pr enhet</label>
              <input type="number" step="0.01" value={justering.kostpris}
                     onChange={(e) => setJustering({ ...justering, kostpris: e.target.value })} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Notat</label>
              <input value={justering.notat}
                     onChange={(e) => setJustering({ ...justering, notat: e.target.value })} />
            </div>
          </div>
        )}
      </Modal>

      {/* Historikk */}
      <Modal
        open={!!histApen} onClose={() => setHistApen(null)} width={680}
        title={`Historikk – ${histApen?.navn || ''}`}
        footer={<button className="btn-ghost" onClick={() => setHistApen(null)}>Lukk</button>}
      >
        <table>
          <thead><tr>
            <th>Dato</th><th>Grunn</th>
            <th style={{ textAlign: 'right' }}>Antall</th>
            <th style={{ textAlign: 'right' }}>Kostpris</th>
            <th>Notat</th>
          </tr></thead>
          <tbody>
            {hist.length === 0 && <tr><td colSpan={5} className="fm-text-muted" style={{ textAlign: 'center', padding: 12 }}>Ingen bevegelser.</td></tr>}
            {hist.map((b) => (
              <tr key={b.id}>
                <td>{b.dato}</td>
                <td><span className="badge badge-neutral">{b.grunn}</span></td>
                <td style={{ textAlign: 'right', color: b.antall < 0 ? 'var(--theme-danger)' : 'var(--theme-success)' }}>
                  {b.antall > 0 ? '+' : ''}{b.antall}
                </td>
                <td style={{ textAlign: 'right' }}>{kr(b.kostpris)}</td>
                <td>{b.notat || (b.referanse_type ? `${b.referanse_type} #${b.referanse_id}` : '')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Modal>
    </>
  );
};

export default Lager;
